/**
 * PasswordField — 표시/숨김 토글이 붙은 비밀번호 입력
 *
 * ui-core `Input` 을 쓰지 않는 이유: `Input` 은 `.field > label / input / .help` 를 한 덩어리로
 * 렌더해 **입력칸만 감싸는 지점이 없다**. 토글을 절대배치하려면 라벨·도움말 유무에 따라
 * 오프셋을 손으로 계산해야 해서 깨지기 쉽다. 그래서 마크업은 `Input` 과 동일하게 유지하되
 * 입력칸만 `position: relative` 로 한 겹 감싼다 — `.field`·`.input`·`.help` 표준 클래스를
 * 그대로 쓰므로 색·높이·포커스 링은 여전히 토큰이 구동한다 (D-09 §15.2).
 *
 * ui-core `Input` 계약에 어도먼트(접미 아이콘) 슬롯이 생기면 이 컴포넌트는 회수 대상이다.
 */
import React, { useId, useState } from 'react';

function EyeIcon({ on }: { on: boolean }) {
  return on ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export interface PasswordFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string;
  /** 오류 문구 — `.help`(status.error). D-09 §15.2 "오류는 도움말 텍스트를 동반한다" */
  error?: string;
  /** 중립 톤 보조 문구. `.help` 는 오류·성공 전용이라 별도 슬롯으로 둔다 */
  hint?: React.ReactNode;
  /** 입력칸 아래 추가 슬롯 — 비밀번호 강도 바 등 */
  footer?: React.ReactNode;
  /** control-height 스케일 — ui-core `Input` 과 같은 계약 (D-08 §6.10) */
  size?: 'sm' | 'md' | 'lg';
}

export const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField({ label, error, hint, footer, size = 'md', className = '', id, required, ...props }, ref) {
    const autoId = useId();
    const inputId = id ?? autoId;
    const [show, setShow] = useState(false);

    return (
      <div className={`field field-fill ${error ? 'error' : ''} ${className}`}>
        {label && (
          <label htmlFor={inputId}>
            {label}
            {required && <span className="req" aria-hidden="true">*</span>}
          </label>
        )}

        <div style={{ position: 'relative' }}>
          <input
            id={inputId}
            ref={ref}
            required={required}
            type={show ? 'text' : 'password'}
            className={`input ${size !== 'md' ? `input-${size}` : ''}`}
            style={{ paddingRight: 'var(--spacing-08)' }}
            {...props}
          />
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShow((v) => !v)}
            aria-label={show ? '비밀번호 숨기기' : '비밀번호 표시'}
            className="text-text-secondary transition-colors hover:text-text"
            style={{
              position: 'absolute', right: 'var(--spacing-04)', top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 0, display: 'flex', alignItems: 'center',
            }}
          >
            <EyeIcon on={show} />
          </button>
        </div>

        {error && <span className="help">{error}</span>}
        {!error && hint && <span className="text-caption text-text-secondary">{hint}</span>}
        {footer}
      </div>
    );
  },
);
